import { useEffect, useRef, useState } from "react";
import { INTERVIEW_CHAPTERS, FINAL_TOUCHES } from "@shared/interview/chapters";
import { HOME_BOARD } from "./planBoard";

/**
 * PLAN-HOME-1 (s228) — THE PLAN'S SIDEBAR, board 12 (Home.dc.html:137-208; rule 127(3)): Home, then "Your plan" — the
 * chapters, each opening to its interview screens — then "Final touches".
 *
 *  • Chapter titles and screen names are board 12's (`HOME_BOARD.chapters`). No counts and no status (s223).
 *  • Chapter one lists the screens the interview HAS: a board name is matched to a built screen by its crumb. Two
 *    board names have no screen yet (Marital status; Military or RCMP service) and are not listed — reported s228.
 *  • Chapters two to six show with the board's screen names, each opening today's placeholder (Reade, ruling D).
 *  • Final touches: board 7's five steps, unnumbered, never counting towards finishing (Reade, ruling D).
 *  • "Across your plan" (s237, ACROSS-YOUR-PLAN-1 stage 1): Where things are (PR B); Contacts follows in PR C, and the
 *    Financial summary and Family meeting in stage 2.
 *  • Not here yet, on purpose: "Sensitive information" (board 8's protected step is not built).
 */

export type SidebarTarget = { kind: "home" } | { kind: "screen"; key: string } | { kind: "touch"; key: string } | { kind: "where" } | { kind: "contacts" };

export function PlanSidebar({
  current,
  onGo,
}: {
  /** What is open: "home", a screen key, or a Final-touches key. */
  current: string;
  onGo: (target: SidebarTarget) => void;
}) {
  const chapterOf = (key: string) => INTERVIEW_CHAPTERS.find((c) => c.screens.some((s) => s.key === key))?.number;
  const [openChapter, setOpenChapter] = useState<number | null>(() => chapterOf(current) ?? null);
  // D-237.56: Final touches is closed until clicked, and open by itself while one of its steps is open (board 12 `touchOpen`).
  const [touchOpen, setTouchOpen] = useState(false);
  const onTouch = FINAL_TOUCHES.some((t) => t.key === current);
  const touchesOpen = touchOpen || onTouch;

  // Rule 123: a sidebar that scrolls says so.
  const scroller = useRef<HTMLElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const measure = () => setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 16);
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => { el.removeEventListener("scroll", measure); window.removeEventListener("resize", measure); };
  }, [openChapter]);

  const item = (on: boolean) =>
    `flex min-h-11 w-full items-center rounded-[8px] text-left hover:bg-warm-stone-light ${on ? "bg-sage-50 text-sage-800" : "text-warm-charcoal"}`;
  const heading = "px-3 pb-1 pt-3.5 text-[13px] uppercase tracking-[0.08em] text-warm-muted";

  return (
    <nav ref={scroller} aria-label="Your plan"
      className="sticky top-0 flex max-h-screen w-[280px] shrink-0 flex-col gap-0.5 overflow-auto border-r border-warm-stone bg-warm-white px-3 py-4"
      data-testid="plan-sidebar">
      <button type="button" onClick={() => onGo({ kind: "home" })} className={`${item(current === "home")} gap-2.5 px-3 text-[15px]`} data-testid="plan-nav-home">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1Z" />
        </svg>
        <span>Home</span>
      </button>

      <span className={heading}>Your plan</span>
      {HOME_BOARD.chapters.map((bc) => {
        const chapter = INTERVIEW_CHAPTERS.find((c) => c.number === bc.number);
        if (!chapter) return null;
        const pending = chapter.screens.every((s) => s.kind === "placeholder");
        // Chapter one: the board's names that the interview has built, in the board's order, opening their screen.
        // Chapters two to six: every board name, each opening the chapter's placeholder.
        const subs = pending
          ? bc.screens.map((name) => ({ name, key: chapter.screens[0].key }))
          : bc.screens.flatMap((name) => {
              const s = chapter.screens.find((x) => x.crumb === name);
              return s ? [{ name, key: s.key }] : [];
            });
        const open = openChapter === bc.number;
        const here = chapterOf(current) === bc.number;
        return (
          <Fold key={bc.number} title={bc.title} open={open} here={here} onToggle={() => setOpenChapter(open ? null : bc.number)}
            testId={`plan-nav-chapter-${bc.number}`}
            items={subs.map((u, i) => ({ key: `${u.name}-${i}`, name: u.name, on: !pending && current === u.key, go: () => onGo({ kind: "screen", key: u.key }),
              testId: `plan-nav-screen-${bc.number}-${i}` }))} />
        );
      })}

      {/* s237 (D-237.56, canvas v467): Final touches folds like a chapter — closed until clicked, open by itself while
          the planner is on one of its steps. The chapters' own fold (rule 159). */}
      <Fold title="Final touches" open={touchesOpen} here={onTouch && !touchesOpen} onToggle={() => setTouchOpen((o) => !o)} testId="plan-nav-touches"
        items={FINAL_TOUCHES.map((t) => ({ key: t.key, name: t.crumb, on: current === t.key, go: () => onGo({ kind: "touch", key: t.key }), testId: `plan-nav-${t.key}` }))} />

      {/* s237 (D-237.57 item 1): board 12 draws Final touches ABOVE the "Across your plan" heading, so it never reads as
          one of its pages. Where things are (PR B), then Contacts (PR C). */}
      <span className={heading} data-testid="plan-nav-across">{HOME_BOARD.where.from}</span>
      <button type="button" onClick={() => onGo({ kind: "where" })} className={`${item(current === "where")} px-3 text-[14.5px]`} data-testid="plan-nav-where">
        {HOME_BOARD.labels.where}
      </button>
      <button type="button" onClick={() => onGo({ kind: "contacts" })} className={`${item(current === "contacts")} px-3 text-[14.5px]`} data-testid="plan-nav-contacts">
        {HOME_BOARD.labels.contacts}
      </button>


      {more && (
        <div className="pointer-events-none sticky -bottom-4 mt-auto flex justify-center bg-gradient-to-b from-transparent to-warm-white to-45% pb-2 pt-[18px]" data-testid="plan-sidebar-more">
          <span className="text-[13px] text-sage-600">More below ↓</span>
        </div>
      )}
    </nav>
  );
}

/** One fold of the sidebar — a chapter, or Final touches (D-237.56): a chevron row that opens its list beneath it. */
function Fold({ title, open, here, onToggle, items, testId }: {
  title: string;
  open: boolean;
  /** The fold holds what is open, but is closed — its row shows as current. */
  here: boolean;
  onToggle: () => void;
  items: { key: string; name: string; on: boolean; go: () => void; testId: string }[];
  testId: string;
}) {
  return (
    <div className="flex flex-col">
      <button type="button" onClick={onToggle} aria-expanded={open}
        className={`flex min-h-11 w-full items-center gap-2 rounded-[8px] pl-2 pr-2.5 text-left hover:bg-warm-stone-light ${here ? "bg-sage-50 text-sage-800" : "text-warm-charcoal"}`} data-testid={testId}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`shrink-0 text-warm-taupe transition-transform ${open ? "rotate-90" : ""}`} aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        <span className="flex-1 text-[14.5px]">{title}</span>
      </button>
      {open && (
        <div className="mb-1.5 ml-5 mt-0.5 flex flex-col border-l border-warm-stone-light">
          {items.map((u) => (
            <button key={u.key} type="button" onClick={u.go}
              className={`flex min-h-11 w-full items-center rounded-r-[8px] pl-3.5 pr-2.5 text-left text-[14px] hover:bg-warm-stone-light ${u.on ? "bg-sage-50 text-sage-800" : "text-warm-charcoal"}`}
              data-testid={u.testId}>
              {u.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
